import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useCurrency } from '@/lib/platform/contexts/currency-context';
import { getCurrencySymbol } from '@/lib/platform/utils/currency';
import { Input, NumberInput, Select } from '@/components/platform/forms';
import Tooltip from '@/components/platform/ui/Tooltip';
import { tooltipData } from '@/lib/platform/data/tooltip-data';
import { TrashIcon } from '@heroicons/react/24/outline';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';
import type { MediaEntryInput, MediaEntryChannel } from '@/lib/platform/types';
import { AddEntryButton } from './AddEntryButton';

/**
 * Multi-entry media channel section (plan 5.5, sketch v2 — uniform two-line
 * entries): line 1 = name input + delete, line 2 = the channel's metric
 * inputs in a responsive grid with small labels ABOVE each input. Each entry
 * framed by a border-only card (SocialDeepDive trigger style, no background),
 * "+ Add X" below. No per-line values or channel sums — totals live in the
 * live calculator.
 *
 * One component for all multi-line channels (tv, webtv, pr, digital,
 * influencer) — channels differ only in CHANNEL_CONFIG.
 */

type NumericEntryField =
  | 'volume'
  | 'cpm'
  | 'duration_min'
  | 'impressions'
  | 'impressions_cpm'
  | 'engagements'
  | 'engagements_cpm';

interface FieldConfig {
  field: NumericEntryField | 'size_category';
  label: string;
  tooltipKey: keyof typeof tooltipData;
  decimals?: number;
  currency?: boolean;
}

interface ChannelConfig {
  title: string;
  badge: 'HQ' | 'MQ';
  description: string;
  /** Label used in the "+ Add X" button (matches the section title wording). */
  addLabel: string;
  fields: FieldConfig[];
  /** Responsive grid classes for line 2 (metric inputs). */
  gridClass: string;
}

const CHANNEL_CONFIG: Record<
  Extract<MediaEntryChannel, 'tv' | 'webtv' | 'pr' | 'digital' | 'influencer'>,
  ChannelConfig
> = {
  tv: {
    title: 'TV',
    badge: 'HQ',
    description: 'Television broadcast duration, size, and viewership metrics.',
    addLabel: 'TV',
    fields: [
      { field: 'duration_min', label: 'Minutes', tooltipKey: 'tvDuration', decimals: 0 },
      { field: 'size_category', label: 'Size', tooltipKey: 'tvSize' },
      { field: 'cpm', label: 'CPM', tooltipKey: 'tvCpm', decimals: 2, currency: true },
      { field: 'volume', label: 'Viewers', tooltipKey: 'tvViewers', decimals: 0 },
    ],
    gridClass: 'grid grid-cols-2 gap-2',
  },
  webtv: {
    title: 'Web TV / Streaming',
    badge: 'HQ',
    description: 'Web TV and streaming platform views and CPM rates.',
    addLabel: 'Web TV / Streaming',
    fields: [
      { field: 'volume', label: 'Views', tooltipKey: 'webtvViews', decimals: 0 },
      { field: 'cpm', label: 'CPM', tooltipKey: 'webtvCpm', decimals: 2, currency: true },
    ],
    gridClass: 'grid grid-cols-2 gap-2',
  },
  pr: {
    title: 'PR / Earned Media',
    badge: 'MQ',
    description: 'Public relations reach and earned media metrics.',
    addLabel: 'PR / Earned Media',
    fields: [
      { field: 'volume', label: 'Reach', tooltipKey: 'prReach', decimals: 0 },
      { field: 'cpm', label: 'CPM', tooltipKey: 'prCpm', decimals: 2, currency: true },
    ],
    gridClass: 'grid grid-cols-2 gap-2',
  },
  digital: {
    title: 'Digital Display',
    badge: 'HQ',
    description: 'Digital display advertising impressions and rates.',
    addLabel: 'Digital Display',
    fields: [
      { field: 'volume', label: 'Impressions', tooltipKey: 'digitalImpressions', decimals: 0 },
      { field: 'cpm', label: 'CPM', tooltipKey: 'digitalCpm', decimals: 2, currency: true },
    ],
    gridClass: 'grid grid-cols-2 gap-2',
  },
  influencer: {
    title: 'Influencers',
    badge: 'HQ',
    description: 'Influencer and creator collaboration reach, impressions, and engagement metrics.',
    addLabel: 'Influencers',
    fields: [
      { field: 'volume', label: 'Reach', tooltipKey: 'influencerReach', decimals: 0 },
      {
        field: 'cpm',
        label: 'Reach CPM',
        tooltipKey: 'influencerReachCpm',
        decimals: 2,
        currency: true,
      },
      {
        field: 'impressions',
        label: 'Impressions',
        tooltipKey: 'influencerImpressions',
        decimals: 0,
      },
      {
        field: 'impressions_cpm',
        label: 'Impr. CPM',
        tooltipKey: 'influencerImpressionsCpm',
        decimals: 2,
        currency: true,
      },
      {
        field: 'engagements',
        label: 'Engagements',
        tooltipKey: 'influencerEngagements',
        decimals: 0,
      },
      {
        field: 'engagements_cpm',
        label: 'Eng. CPM',
        tooltipKey: 'influencerEngagementsCpm',
        decimals: 2,
        currency: true,
      },
    ],
    gridClass: 'grid grid-cols-2 gap-2',
  },
};

const TV_SIZE_OPTIONS = [
  { value: 'S', label: 'Small (≤1%)' },
  { value: 'M', label: 'Medium (1-5%)' },
  { value: 'L', label: 'Large (5-15%)' },
  { value: 'XL', label: 'Extra Large (>15%)' },
];

interface MediaEntrySectionProps {
  channel: keyof typeof CHANNEL_CONFIG;
  /** This channel's entry lines only. */
  entries: MediaEntryInput[];
  onAddEntry: () => void;
  onUpdateEntry: (index: number, patch: Partial<MediaEntryInput>) => void;
  onDeleteEntry: (index: number) => void;
  canEdit: boolean;
  numberFormat?: NumberFormatType;
}

export function MediaEntrySection({
  channel,
  entries,
  onAddEntry,
  onUpdateEntry,
  onDeleteEntry,
  canEdit,
  numberFormat = 'eu',
}: MediaEntrySectionProps) {
  const { darkMode } = useDarkMode();
  const { baseCurrency } = useCurrency();
  const config = CHANNEL_CONFIG[channel];

  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-10 border-b pb-12 md:grid-cols-3 ${darkMode ? 'border-white/10' : 'border-gray-900/10'} ${!canEdit ? 'opacity-70' : ''}`}
    >
      <div>
        <h2 className={`text-base/7 font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
          {config.title}
          <span
            className={`ml-2 rounded px-2 py-1 text-xs font-medium ${
              darkMode ? 'bg-white/10 text-gray-300' : 'bg-black/6 text-gray-600'
            }`}
          >
            {config.badge}
          </span>
        </h2>
        <p className={`mt-1 text-sm/6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {config.description}
        </p>
        {!canEdit && (
          <span className="mt-2 inline-block rounded bg-red-500/10 px-2 py-1 text-xs font-medium text-red-500">
            Read Only
          </span>
        )}
      </div>

      <div className="max-w-2xl md:col-span-2">
        {entries.length > 0 && (
          <div className="space-y-8">
            {entries.map((entry, index) => (
              <div
                key={index}
                className={`space-y-2 rounded-lg border p-4 ${
                  darkMode ? 'border-white/10' : 'border-gray-200'
                }`}
              >
                {/* Line 1: name + delete */}
                <div className="flex items-end gap-2">
                  {/* data-ai-field tags match the AI form bridge names, e.g. tv[0].name */}
                  <div className="min-w-0 flex-1" data-ai-field={`${channel}[${index}].name`}>
                    <Input
                      label="Media"
                      value={entry.name}
                      onChange={(e) => onUpdateEntry(index, { name: e.target.value })}
                      placeholder="Enter media name"
                      aria-label={`${config.title} entry ${index + 1} name`}
                      disabled={!canEdit}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeleteEntry(index)}
                    disabled={!canEdit}
                    aria-label={`Delete ${config.title} entry ${index + 1}`}
                    className={`shrink-0 rounded-lg p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      darkMode
                        ? 'text-gray-500 hover:bg-white/5 hover:text-red-400'
                        : 'text-gray-400 hover:bg-gray-50 hover:text-red-500'
                    }`}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
                {/* Line 2: metric inputs with small labels above */}
                <div className={config.gridClass}>
                  {config.fields.map((fieldConfig) => (
                    <div
                      key={fieldConfig.field}
                      data-ai-field={`${channel}[${index}].${fieldConfig.field}`}
                    >
                      {fieldConfig.field === 'size_category' ? (
                        <Select
                          label={
                            <>
                              {fieldConfig.label}
                              <Tooltip {...tooltipData[fieldConfig.tooltipKey]} wide={true} />
                            </>
                          }
                          value={entry.size_category || 'M'}
                          onChange={(value) =>
                            onUpdateEntry(index, {
                              size_category: value as 'S' | 'M' | 'L' | 'XL',
                            })
                          }
                          options={TV_SIZE_OPTIONS}
                          disabled={!canEdit}
                        />
                      ) : (
                        <NumberInput
                          label={
                            <>
                              {fieldConfig.label}
                              <Tooltip {...tooltipData[fieldConfig.tooltipKey]} wide={true} />
                            </>
                          }
                          value={entry[fieldConfig.field] ?? 0}
                          onChange={(value) =>
                            onUpdateEntry(index, {
                              [fieldConfig.field]: typeof value === 'number' ? value : 0,
                            })
                          }
                          decimals={fieldConfig.decimals ?? 0}
                          numberFormat={numberFormat}
                          suffix={
                            fieldConfig.currency ? getCurrencySymbol(baseCurrency) : undefined
                          }
                          placeholder="0"
                          disabled={!canEdit}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {entries.length > 0 ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={onAddEntry}
              disabled={!canEdit}
              className={`w-full px-3 py-2 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                darkMode
                  ? 'text-indigo-400 hover:text-indigo-300'
                  : 'text-indigo-600 hover:text-indigo-500'
              }`}
            >
              + Add {config.addLabel}
            </button>
          </div>
        ) : (
          <AddEntryButton label={config.addLabel} onClick={onAddEntry} disabled={!canEdit} />
        )}
      </div>
    </div>
  );
}
